/**
 * The words on the first screen of each shared form.
 *
 * Here rather than inside the screens because two places draw them: the form
 * itself, and the phone preview on the My brand screen's Form tab that shows a
 * practitioner what a client will see. The preview cannot be the real
 * component - it is a 390px layout scaled down inside a panel, and the real
 * one's `sm:` rules read the window rather than the box it sits in - so the
 * layout is drawn twice on purpose. The words are not.
 */

/**
 * Three lines on a phone, and every break in them is placed rather than left
 * to the column.
 *
 * "Hi," keeps its own line at every width. The question under it is broken by
 * hand for the same reason the pre-consultation heading is: a question left to
 * wrap falls differently on every handset — "how we may / help you?" on a
 * 390px screen and "how we may help / you?" on a 430px one, where a lone
 * "you?" reads as a line that got away rather than as a line that was set.
 *
 * That second break is the phone's only. From `sm:` up the design puts the
 * whole question on one line, and a hard break there would leave "how we may"
 * hanging over a column with room for the rest of it.
 */
export const ENQUIRY_WELCOME_GREETING = "Hi,";
export const ENQUIRY_WELCOME_QUESTION = "how we may";
export const ENQUIRY_WELCOME_QUESTION_TAIL = "help you?";

/** The practitioner speaking in the first person - no brand name in it. */
export const ENQUIRY_WELCOME_SUBTITLE =
  "Help me understand your goals & requirements";

/** The button both welcome screens end on. */
export const FORM_WELCOME_ACTION = "Continue";

/* ── The pre-consultation form ─────────────────────────────────────
   No longer provisional: `PreconsultationWelcome` reads these, and so does the
   Form tab's preview, so a practitioner is shown the sentence their client is
   actually sent rather than a second copy of it that drifts.            */

/**
 * The heading, over two lines as the design sets it.
 *
 * Not the reader's name, which the enquiry form's greeting is and this one
 * used to be. A pre-consultation is sent to somebody already on the books —
 * they know who they are and who is asking — so the heading is spent on the
 * one thing they do not know yet, which is that this is the start of something
 * rather than another message to read.
 */
export const PRE_CONSULT_WELCOME_HEADING = "Let\u2019s";
export const PRE_CONSULT_WELCOME_HEADING_TAIL = "get started!";

/**
 * The line under it, which is where the name went.
 *
 * Set smaller and in the brand's own teal rather than the heading's near-black:
 * it is an aside to somebody the practitioner already knows, not the headline.
 * Dropped whole where no first name is on file — "Hi," alone is a greeting
 * that lost its second half, and the heading carries the screen without it.
 */
export const PRE_CONSULT_WELCOME_GREETING = "Hi,";

/**
 * A stand-in client for the Form tab's preview, and nowhere else.
 *
 * The real screen names the reader under the heading - see
 * `PreconsultationWelcome` - and a preview has no reader to name. Left out,
 * the practitioner is shown a screen with a line missing from it rather than
 * the one their client is actually sent.
 */
export const PRE_CONSULT_PREVIEW_NAME = "Riya";

export const PRE_CONSULT_WELCOME_SUBTITLE =
  "Share your health, lifestyle, food preferences & allergies.";
